import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
export type MarketType = "single-binary" | "grouped-binary" | "neg-risk";
export interface SubMarket {
  id: string;
  conditionId: string;
  question: string;
  outcomes: string[];
  outcomePrices: string[];
  tokenIds: string[];
  negRisk: boolean;
  groupItemTitle: string;
}
interface MarketData {
  title: string;
  outcomes: string[];
  outcomePrices: string[];
  tokenIds: string[];
  markets: SubMarket[];
  isMultiMarket: boolean;
  enableNegRisk: boolean;
  marketType: MarketType;
}
interface PolymarketEventResponse {
  id: string;
  title: string;
  slug: string;
  negRisk?: boolean;
  enableNegRisk?: boolean;
  markets: Array<{
    id: string;
    conditionId?: string;
    question: string;
    outcomes: string | string[];
    outcomePrices?: string | string[] | number[];
    clobTokenIds: string | string[];
    negRisk: boolean;
    groupItemTitle: string;
    closed?: boolean;
    // Fallback price fields when outcomePrices is missing
    bestBid?: number;
    bestAsk?: number;
    lastTradePrice?: number;
  }>;
}
interface MidpointResponse {
  mid?: string;
}
const POLYMARKET_CLOB_HOST = "https://clob.polymarket.com";

/**
 * Extract event slug from a Polymarket URL
 */
function extractSlugFromUrl(urlOrSlug: string): string | null {
  if (urlOrSlug.includes("polymarket.com")) {
    // Extract the last path segment as the slug (works for /event/..., /sports/.../, etc.)
    try {
      const url = new URL(urlOrSlug);
      const segments = url.pathname.split("/").filter(Boolean);
      return segments.length > 0 ? segments[segments.length - 1] : null;
    } catch {
      // Not a valid URL, try regex fallback
      const match = urlOrSlug.match(/polymarket\.com\/(?:.+\/)?([a-zA-Z0-9-]+)\/?$/);
      return match ? match[1] : null;
    }
  }
  return urlOrSlug.trim() || null;
}

/**
 * Hook to fetch Polymarket market data from event URL/slug
 */
export function usePolymarketMarket(marketUrl: string) {
  const slug = extractSlugFromUrl(marketUrl);
  return useQuery<MarketData | null>({
    queryKey: ["polymarket-market", slug],
    queryFn: async () => {
      if (!slug) return null;
      const response = await fetch(`/api/polymarket/events/${encodeURIComponent(slug)}`);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Market not found");
        }
        throw new Error(`Error: ${response.status}`);
      }
      const data: PolymarketEventResponse = await response.json();
      if (!data.markets || data.markets.length === 0) {
        throw new Error("No markets found");
      }

      // Parse fields — handle JSON strings, already-parsed arrays, and missing values
      const parseField = (val: string | string[] | number[] | undefined | null): string[] => {
        if (!val) return [];
        if (Array.isArray(val)) return val.map(String);
        try {
          const parsed = JSON.parse(val);
          return Array.isArray(parsed) ? parsed.map(String) : [];
        } catch {
          return [];
        }
      };
      const enableNegRisk = !!(data.enableNegRisk ?? data.negRisk);

      // Filter out closed/resolved markets (fall back to all if none remain)
      const openMarkets = data.markets.filter(m => !m.closed);
      const activeMarkets = openMarkets.length > 0 ? openMarkets : data.markets;
      const subMarkets: SubMarket[] = activeMarkets.map(m_0 => {
        const outcomes = parseField(m_0.outcomes);
        let outcomePrices = parseField(m_0.outcomePrices);

        // Fallback: derive outcomePrices from bestBid/lastTradePrice when missing
        if (outcomePrices.length === 0 && outcomes.length > 0) {
          const yesPrice = m_0.bestBid ?? m_0.lastTradePrice ?? 0;
          outcomePrices = outcomes.map((_, i) => i === 0 ? String(yesPrice) : String(Math.max(0, 1 - yesPrice)));
        }
        return {
          id: m_0.id,
          conditionId: m_0.conditionId || "",
          question: m_0.question,
          outcomes,
          outcomePrices,
          tokenIds: parseField(m_0.clobTokenIds),
          negRisk: !!m_0.negRisk,
          groupItemTitle: m_0.groupItemTitle || ""
        };
      });

      // Sort multi-market events by "Yes" price descending (highest probability first)
      if (subMarkets.length > 1) {
        subMarkets.sort((a, b) => {
          const priceA = parseFloat(a.outcomePrices[0]) || 0;
          const priceB = parseFloat(b.outcomePrices[0]) || 0;
          return priceB - priceA;
        });
      }

      // Top-level fields from first market for backward compatibility
      const first = subMarkets[0];
      const isMulti = subMarkets.length > 1;

      // Determine market type
      let marketType: MarketType = "single-binary";
      if (isMulti) {
        marketType = enableNegRisk ? "neg-risk" : "grouped-binary";
      } else if (enableNegRisk) {
        marketType = "neg-risk";
      }
      return {
        title: data.title,
        outcomes: first.outcomes,
        outcomePrices: first.outcomePrices,
        tokenIds: first.tokenIds,
        markets: subMarkets,
        isMultiMarket: isMulti,
        enableNegRisk,
        marketType
      };
    },
    enabled: !!slug,
    staleTime: 60 * 1000,
    // 1 minute - market metadata doesn't change often
    retry: 1
  });
}

/**
 * Hook to fetch current midpoint price from Polymarket CLOB API
 * Polls every 3 seconds for real-time price updates
 * See: https://docs.polymarket.com/quickstart/reference/endpoints#clob-api
 */
export function usePolymarketMidpoint(tokenId, t0) {
  const $ = _c(7);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  let t2;
  if ($[0] !== tokenId) {
    t1 = ["polymarket-midpoint", tokenId];
    t2 = async () => {
      if (!tokenId) {
        return null;
      }
      const response = await fetch(`${POLYMARKET_CLOB_HOST}/midpoint?token_id=${tokenId}`);
      if (!response.ok) {
        console.error(`[usePolymarketMidpoint] Failed to fetch midpoint: ${response.status}`);
        return null;
      }
      const data = await response.json();
      if (data.mid) {
        return parseFloat(data.mid);
      }
      return null;
    };
    $[0] = tokenId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  const t3 = enabled && !!tokenId;
  let t4;
  if ($[3] !== t1 || $[4] !== t2 || $[5] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      refetchInterval: 3000,
      staleTime: 1000,
      retry: false
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return useQuery(t4);
}